import moment, { type Moment } from "moment";
import FieldNames from "../defaults/FieldNames.json";
import {
  getUTCMoment,
  getStartOrEndOfFiscalYear, getFiscalYear,
} from "./DateHelper";
import type Graphic from "@arcgis/core/Graphic";

/**
 * Sets the class for a field depending on if it is missing a value or modified since the last save
 * @param isRequired - Whether the field is required
 * @param detail - The object containing feature data. Expects detail.attributes to exist.
 * @param field - Which field is being validated
 * @param classes - Styling classes for missing values
 * @param notSavedClasses - Styling classes for unsaved values
 * @param treatmentValidationUnsavedValues - List of unsaved treatment values
 * @param actualsTreatmentValidationUnsavedValues - List of unsaved actual treatment values
 * @param activityValidationUnsavedValues - List of unsaved activity values
 * @param actualsActivityValidationUnsavedValues - List of unsaved actual activity values
 * @returns Returns an appropriate class if the field is unsaved/missing, a blank string otherwise
 */
export function getFieldStyle(isRequired, detail, field, classes, notSavedClasses, treatmentValidationUnsavedValues, actualsTreatmentValidationUnsavedValues, activityValidationUnsavedValues, actualsActivityValidationUnsavedValues) {
  let tempClassName = '';
  if(isRequired && inputValueMissing(detail, field)){
    tempClassName = classes;
  } else if(showNotSavedClasses(field, detail, treatmentValidationUnsavedValues, actualsTreatmentValidationUnsavedValues, activityValidationUnsavedValues, actualsActivityValidationUnsavedValues)) {
    tempClassName = notSavedClasses;
  }

  return tempClassName;
}

/** 
 * Determines if the detail object is missing a value for the given field
 * @param detail - The object containing feature data. Expects detail.attributes to exist.
 * @param field - Which field is being validated
 * @returns Returns true if the field is missing a value
 */
export function inputValueMissing(detail, field) {
  if(detail && detail.attributes){
    if(!detail.attributes[field]){
      return true
    } else {
      return false
    }
  } else {
    return false;
  }
}

/** 
 * Determines if the passed in field has been modified since the last save
 * @param field - Which field is being validated
 * @param detail - The object containing feature data. Expects detail.attributes to exist.
 * @param treatmentValidationUnsavedValues - List of unsaved treatment values
 * @param actualsTreatmentValidationUnsavedValues - List of unsaved actual treatment values
 * @param activityValidationUnsavedValues - List of unsaved activity values
 * @param actualsActivityValidationUnsavedValues - List of unsaved actual activity values
 * @returns Returns true if the field has been modified but not saved, false otherwise
 */
export function showNotSavedClasses(field, detail, treatmentValidationUnsavedValues, actualsTreatmentValidationUnsavedValues, 
  activityValidationUnsavedValues, actualsActivityValidationUnsavedValues) {
  var fieldState = false;
  if(detail && detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.treatment)
  {
    // Estimated Treatment
    if(treatmentValidationUnsavedValues === null || !field || field.length === 0)
    {
      return false;
    }
    fieldState = treatmentValidationUnsavedValues[field];
    return fieldState;
  }
  else if(detail && detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment)
  {
    // Actual Treatment
    if(actualsTreatmentValidationUnsavedValues === null || !field || field.length === 0)
    {
      return false;
    }
    fieldState = actualsTreatmentValidationUnsavedValues[field];
    return fieldState;
  }
  else if(detail && detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.activity)
  {
    // Estimated Activity
    if(activityValidationUnsavedValues === null || !field || field.length === 0)
    {
      return false;
    }
    fieldState = activityValidationUnsavedValues[field];
    return fieldState;
  }
  else if(detail && detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity)
  {
    // Actual Activity
    if(actualsActivityValidationUnsavedValues === null || !field || field.length === 0)
    {
      return false;
    }
    fieldState = actualsActivityValidationUnsavedValues[field];
    return fieldState;
  }
  else
  {
    return false;
  }
}

export function validateInitiationDate(value: Date | null, detail: Graphic, isActual = false) {
  if (!value || !(detail && detail.attributes[FieldNames.featureFields.completionDate])) {
    return null;
  }

  const momentCompletionDate: Moment = getUTCMoment(detail.attributes[FieldNames.featureFields.completionDate]);
  const momentValue: Moment = getUTCMoment(value);

  return momentValue.isAfter(momentCompletionDate, 'day') ? (isActual ? "The actual Initiation Date cannot be after the actual Completion Date" : "The estimated Initiation Date cannot be after the estimated Completion Date") : null;
}


export function validateCompletionDate(value: Date | null, detail: Graphic, isActual: boolean = false,) {
  if (!value) {
    return null;
  }
  const initiationDate = detail.attributes[FieldNames.featureFields.initiationDate];
  if (!initiationDate) {
    return "The Initiation Date must be entered";
  }

  const momentInitiationDate: Moment = getUTCMoment(initiationDate);
  const momentVal: Moment = getUTCMoment(value);

  return momentVal.isBefore(momentInitiationDate, 'day') ? (isActual ? "The actual Completion Date cannot be before the actual Initiation Date" : "The estimated Completion Date cannot be before the estimated Initiation Date") : null;
}

export function validateUnapprovalDate(value: Date) {
  if (!value) {
    return null;
  }

  return !moment(value).utc().isSame(moment().utc(), 'day') ? "The UnApproval Date must be today" : null;
}


export function validateLocalApprovalDate(value: Date | null, detail: Graphic): string[] {
   const errors: string[] = [];
  if (!value) {
    return errors;
  }

  const momentValue: Moment = getUTCMoment(value);
  let now = moment().utc();
  const momentRegionalApprovalDate: Moment | null = detail && detail.attributes[FieldNames.featureFields.regionalApprovalDate] ? getUTCMoment(detail.attributes[FieldNames.featureFields.regionalApprovalDate]) : null;
  if (momentValue.isAfter(now, 'day')) {
    errors.push("The Local Approval Date may not be in the future");
  }

  momentRegionalApprovalDate && momentValue.isAfter(momentRegionalApprovalDate, 'day') ? errors.push("The Local Approval Date must be the same date or prior to the Regional Approval Date") : null;
  return errors;
}

export function validateRegionalApprovalDate(value: Date | null, detail: Graphic): string[]{
  const errors: string[] = [];
  if (!value) {
    return errors;
  }
  const momentValue: Moment = getUTCMoment(value);
  let now = moment().utc();
    const localApprovalDate: Moment | null = detail && detail.attributes[FieldNames.featureFields.localApprovalDate] ? getUTCMoment(detail.attributes[FieldNames.featureFields.localApprovalDate]) : null;
    const agencyApprovalDate: Moment | null = detail && detail.attributes[FieldNames.featureFields.agencyApprovalDate] ? getUTCMoment(detail.attributes[FieldNames.featureFields.agencyApprovalDate]) : null;
  if (momentValue.isAfter(now, 'day')) {
    errors.push("The Regional Approval Date may not be in the future");
  }
  if (localApprovalDate && momentValue.isBefore(localApprovalDate, 'day')) {
    errors.push("The Regional Approval Date must be the same date or following the Local Approval Date");
  }

  if (agencyApprovalDate && momentValue.isAfter(agencyApprovalDate, 'day')) {
    errors.push("The Regional Approval Date must be the same date or prior to the Agency Approval Date");
  }
  return errors;
}

export function validateAgencyApprovalDate(value: Date | null, detail: Graphic): string[] {
  const errors : string[] = []
  if (!value) {
    return errors;
  }
  const momentRegional = detail && detail.attributes[FieldNames.featureFields.regionalApprovalDate] ? getUTCMoment(detail.attributes[FieldNames.featureFields.regionalApprovalDate]) : null;
  const momentLocal = detail && detail.attributes[FieldNames.featureFields.localApprovalDate] ? getUTCMoment(detail.attributes[FieldNames.featureFields.localApprovalDate]) : null;
  const momentValue: Moment = getUTCMoment(value);
  let now = moment().utc();

    if (momentValue.isAfter(now, 'day')) {
      errors.push("The Agency Approval Date may not be in the future");
    }

    if (momentRegional && momentValue.isBefore(momentRegional, 'day')) {
      errors.push("The Agency Approval Date must be the same date or following the Regional Approval Date");
    }
    if (momentLocal && momentValue.isBefore(momentLocal, 'day')) {
      errors.push("The Agency Approval Date must be the same date or following the Local Approval Date");
    }

    return errors;
}

export function validateDepartmentApprovalDate(value: Date | null, detailObj: Graphic): string[] {

  const errors: string[] = [];
  if (!value) {
    return errors;
  }
  const regionalApprovalDate: Moment | null = detailObj && detailObj.attributes[FieldNames.featureFields.regionalApprovalDate] ? getUTCMoment(detailObj.attributes[FieldNames.featureFields.regionalApprovalDate]) : null;
  const agencyApprovalDate: Moment | null = detailObj && detailObj.attributes[FieldNames.featureFields.agencyApprovalDate] ? getUTCMoment(detailObj.attributes[FieldNames.featureFields.agencyApprovalDate]) : null;
  const momentValue: Moment = getUTCMoment(value);
  const now = moment().utc();
  if (momentValue.isAfter(now, 'day')) {
    errors.push("The Department Approval Date may not be in the future");
  }
  if (agencyApprovalDate && momentValue.isBefore(agencyApprovalDate, 'day')) {
    errors.push("The Department Approval Date must be the same date or after the Agency Approval Date");
  }

  if (regionalApprovalDate && momentValue.isBefore(regionalApprovalDate, 'day')) {
    errors.push("The Department Approval Date must be the same date or following the Regional Approval Date");
  }
  return errors;
}

export const isValidCarryoverDateValue = (value: Date | null | number, feature = null): string | null => {
  if (!value) {
    return null;
  }
  let existingInitDateParse: Moment  | null = feature && feature[FieldNames.featureFields.initiationDate] ?
    getUTCMoment(feature[FieldNames.featureFields.initiationDate]) : null
  let valueParse = getUTCMoment(value);

  // Initiation date validation
  if(valueParse.isBefore(existingInitDateParse, 'day'))
  {
    return "The date must be on or after the existing initiation date for the selected feature";
  }
  return null;
}

export const isValidUndoCarryoverDateValue = (value: Date | null) => {
    if (!value) {
      return null;
    }
  const valueParse: Moment = getUTCMoment(value);
  const currentFY = getFiscalYear(new Date());
  const minDate: Moment = getUTCMoment(getStartOrEndOfFiscalYear(currentFY, true));
  const maxDate: Moment = getUTCMoment(getStartOrEndOfFiscalYear(currentFY, false));

  // Initiation date validation
  if(valueParse.isBefore(minDate, 'day') || valueParse.isAfter(maxDate, 'day'))
  {
    return "Selected date must be in the current Fiscal Year";
  }
  return null;
}
  
export const isValidBulkCarryoverDateValue = (value: Date | number | null, allSelectedItems: any[]) : string[] => {
  const errors: string[] = [];
  if (!value){
    return errors;
  }


  let maxInitDateParse: null | Moment = null;
  allSelectedItems.forEach((carryoverRow: any) => {
    let existingInitDateParse = getUTCMoment(carryoverRow[FieldNames.featureFields.initiationDate]);
    if(!maxInitDateParse || (maxInitDateParse && existingInitDateParse.isSameOrAfter(maxInitDateParse, 'day')))
    {
      maxInitDateParse = existingInitDateParse;
    }
  });

  let valueParse = getUTCMoment(value);

  if(!maxInitDateParse)
  {
    errors.push("Error: Couldn't find InitiationDate for any of the selected rows");
  }

  // Initiation date validation  
  if(maxInitDateParse && valueParse.isBefore(maxInitDateParse,  'day'))
  {
    errors.push(`The selected date is before one of the initiation dates for the selected rows. The initiation date must be on or after: ${maxInitDateParse.format('MM/DD/YYYY')}`);
  }

  return errors
}
